import React, { useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@rtkwlf/pro-solid-svg-icons/faLock';
import { useTranslation } from 'react-i18next';
import {
  Link,
  matchPath,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';

import { useHasCapability } from '@apis/capability/use-has-capability/use-has-capability';
import { useLegacyCapabilities } from '@apis/rendall/queries/use-capabilities';
import { Stack, Tab, Tabs, Typography } from '@packages/fenrir/components';
import { PageContent } from '@packages/fenrir/page-blocks/page/page-content';
import { useFeatureFlag } from '@packages/platform/feature-flags';
import { hasAnyRequiredProperty } from '@packages/utils/has-any-required-property';
import { ContactConciergeLink } from 'ae-components/contact/contact-concierge-link';

import type { AgentSettingsPageInfoType } from './use-agent-settings-pages';
import { useAgentSettingsPages } from './use-agent-settings-pages';

export const AGENT_SETTINGS_READ_CAPABILITY = 'agent_settings_read';
export const AGENT_SETTINGS_WRITE_CAPABILITY = 'agent_settings_write';

declare module '@platform/core/feature-flags' {
  interface FeatureFlags {
    UNIFIED_PORTAL_IA?: boolean;
  }
}

function useRouteMatch(patterns: readonly string[]) {
  const { pathname } = useLocation();

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    const possibleMatch = matchPath(pattern, pathname);
    if (possibleMatch !== null) {
      return possibleMatch;
    }
  }

  return null;
}

const AgentSettingsLayout = () => {
  const [unifiedPortalIaFF] = useFeatureFlag('UNIFIED_PORTAL_IA');
  const { t } = useTranslation('telemetryAgents');
  const settingsPages = useAgentSettingsPages();
  const routeMatch = useRouteMatch(
    settingsPages.map(({ linkPattern }) => linkPattern),
  );
  const currentPagePattern = routeMatch?.pattern?.path;
  const currentPageInfo: AgentSettingsPageInfoType | undefined = useMemo(
    () =>
      currentPagePattern === undefined
        ? undefined
        : settingsPages.find(
            ({ linkPattern }) => linkPattern === currentPagePattern,
          ),
    [settingsPages, currentPagePattern],
  );
  const {
    data: legacyCapabilitiesData,
    isFetched: isLegacyCapabilitiesFetched,
  } = useLegacyCapabilities();
  const {
    hasCapability: hasAgentSettingsRead,
    isFetched: isCapabilitiesFetched,
  } = useHasCapability(AGENT_SETTINGS_READ_CAPABILITY);
  const allowedPages = useMemo(
    () =>
      settingsPages.filter(
        ({ legacyCapabilityRequirements, internalOnly, showNavbar }) => {
          if (!internalOnly && hasAgentSettingsRead) {
            return true;
          }
          if (Object.keys(legacyCapabilityRequirements).length > 0) {
            return hasAnyRequiredProperty(
              legacyCapabilityRequirements,
              legacyCapabilitiesData,
            );
          }
          return false;
        },
      ),
    [hasAgentSettingsRead, legacyCapabilitiesData, settingsPages],
  );
  if (!isLegacyCapabilitiesFetched || !isCapabilitiesFetched) {
    return null;
  }
  if (allowedPages.length === 0) {
    return <Navigate to="/403" replace />;
  }
  if (
    currentPagePattern === null ||
    currentPagePattern === undefined ||
    currentPageInfo === undefined
  ) {
    return <Navigate to={allowedPages[0].link} replace />;
  }
  const { showNavbar, breadcrumbs } = currentPageInfo;
  return (
    <Stack rowGap="24px">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack rowGap="8px">
          <Typography variant="h3">
            {unifiedPortalIaFF
              ? t('app:navigation.dataCollection')
              : t('telemetryAgents:agentSettings.title')}
          </Typography>
          {breadcrumbs()}
        </Stack>
        <ContactConciergeLink />
      </Stack>
      <Stack
        bgcolor={(theme) => theme.palette.background.default}
        padding="24px"
        borderRadius="4px"
      >
        {showNavbar && (
          <>
            <Typography variant="body1" fontWeight="bold">
              {t('telemetryAgents:agentSettings.contentTitle')}
            </Typography>
            <Tabs
              value={currentPagePattern}
              indicatorColor={
                currentPageInfo?.internalOnly ? 'secondary' : 'primary'
              }
              textColor={
                currentPageInfo?.internalOnly ? 'secondary' : 'primary'
              }
              sx={{ marginTop: '1' }}
            >
              {allowedPages.map(
                ({ key, internalOnly, link, linkPattern, showNavbar }) =>
                  showNavbar ? (
                    <Tab
                      key={key}
                      label={t(
                        `telemetryAgents:agentSettings.tabTitles.${key}`,
                      )}
                      icon={
                        internalOnly ? (
                          <FontAwesomeIcon icon={faLock} />
                        ) : undefined
                      }
                      iconPosition={internalOnly ? 'end' : undefined}
                      value={linkPattern}
                      to={link}
                      component={Link}
                    />
                  ) : null,
              )}
            </Tabs>
          </>
        )}
        <Stack>
          <Outlet />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AgentSettingsLayout;

export const Component = () => {
  return (
    <PageContent>
      <AgentSettingsLayout />
    </PageContent>
  );
};
